import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaTachometerAlt,
    FaUsers,
    FaChartBar,
    FaExchangeAlt,
    FaArrowDown,
    FaArrowUp,
    FaChartLine,
    FaWallet,
    FaTicketAlt,
    FaTimes,
    FaChevronDown,
    FaBroadcastTower,
    FaListAlt,
} from "react-icons/fa";

const links = [
    { to: "/", label: "Dashboard", icon: <FaTachometerAlt /> },
    { to: "/users", label: "Users", icon: <FaUsers /> },
    {
        label: "Statistics",
        icon: <FaChartBar />,
        children: [
            { to: "/stats-trade", label: "Trade", icon: <FaChartLine /> },
            { to: "/stats-transaction", label: "Transaction", icon: <FaExchangeAlt /> },
        ],
    },
    {
        label: "Transactions",
        icon: <FaExchangeAlt />,
        children: [
            { to: "/deposits", label: "Deposits", icon: <FaArrowDown /> },
            { to: "/withdrawals", label: "Withdrawals", icon: <FaArrowUp /> },
        ],
    },
    {
        label: "Trade",
        icon: <FaExchangeAlt />,
        children: [
            { to: "/trade-list", label: "Trade List", icon: <FaListAlt /> },
            { to: "/live-trades-all", label: "Live Trades", icon: <FaBroadcastTower /> },
        ],
    },
    { to: "/salary", label: "Salary", icon: <FaWallet /> },
    { to: "/coupon", label: "Coupon", icon: <FaTicketAlt /> },
];

function SidebarContent({ collapsed, onNavigate }) {
    const location = useLocation();
    const rootRef = useRef(null);
    const hoverCloseTimerRef = useRef(null);

    const menuLabels = useMemo(() => links.filter((l) => l.children?.length).map((l) => l.label), []);

    const getActiveMenusFromRoute = () => {
        const next = {};
        menuLabels.forEach((label) => (next[label] = false));
        links.forEach((l) => {
            if (l.children?.length) {
                next[l.label] = l.children.some((c) => location.pathname === c.to);
            }
        });
        return next;
    };

    const [openMenus, setOpenMenus] = useState(() => getActiveMenusFromRoute());

    // Auto-close dropdowns when route changes; only keep active parent open
    useEffect(() => {
        setOpenMenus(getActiveMenusFromRoute());
    }, [location.pathname]);

    const [hoverMenu, setHoverMenu] = useState(null); // { label, top, left, children }

    const clearHoverCloseTimer = () => {
        if (hoverCloseTimerRef.current) {
            clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
        }
    };

    const scheduleHoverClose = () => {
        clearHoverCloseTimer();
        hoverCloseTimerRef.current = setTimeout(() => setHoverMenu(null), 120);
    };

    const handleNavigate = () => {
        setHoverMenu(null);
        clearHoverCloseTimer();
        if (typeof onNavigate === "function") onNavigate();
    };

    const toggleMenu = (label) => {
        setHoverMenu(null);
        setOpenMenus((prev) => {
            const next = {};
            menuLabels.forEach((k) => (next[k] = false));
            next[label] = !prev[label];
            return next;
        });
    };

    const openHoverMenu = (e, label, children) => {
        if (!collapsed) return;
        const itemRect = e.currentTarget.getBoundingClientRect();
        const rootRect = rootRef.current?.getBoundingClientRect();
        const left = (rootRect?.right ?? itemRect.right) + 10;

        clearHoverCloseTimer();
        setHoverMenu({
            label,
            top: Math.max(8, itemRect.top),
            left,
            children,
        });
    };

    return (
        <div className="h-100 d-flex flex-column" style={{ minHeight: 0 }} ref={rootRef}>
            <style>{`
        .sidebar-scroll {
          scrollbar-width: none;        /* Firefox */
          -ms-overflow-style: none;     /* IE/Edge legacy */
        }
        .sidebar-scroll::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;                /* Chrome/Safari */
        }
        .sidebar-hover-menu {
          min-width: 220px;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 12px 28px rgba(0,0,0,0.12);
          border-radius: 12px;
          overflow: hidden;
          background: #fff;
        }
        .sidebar-hover-header {
          padding: 10px 12px;
          font-weight: 600;
          border-bottom: 1px solid rgba(0,0,0,0.06);
          background: #fff;
        }
      `}</style>
            {/* Scrollable area (unlimited items) + scrollbar hidden */}
            <div className="p-2 flex-grow-1 bg-white overflow-auto sidebar-scroll" style={{ minHeight: 0 }}>
                {links.map((l) => {
                    if (l.children?.length) {
                        const anyChildActive = l.children.some((c) => location.pathname === c.to);
                        const isOpen = !!openMenus[l.label];

                        return (
                            <div key={l.label} className="mb-1">
                                <button
                                    type="button"
                                    onClick={() => (!collapsed ? toggleMenu(l.label) : undefined)}
                                    onMouseEnter={(e) => openHoverMenu(e, l.label, l.children)}
                                    onMouseLeave={() => (collapsed ? scheduleHoverClose() : undefined)}
                                    className={[
                                        "w-100 d-flex align-items-center rounded px-3 py-2 text-decoration-none border-0",
                                        collapsed ? "justify-content-center" : "gap-2",
                                        anyChildActive ? "bg-primary text-white" : "text-dark hover-bg",
                                    ].join(" ")}
                                    style={{ background: "transparent" }}
                                    title={collapsed ? l.label : undefined}
                                >
                                    <span className="fs-6">{l.icon}</span>

                                    {!collapsed && (
                                        <>
                                            <span className="fw-medium flex-grow-1 text-start">{l.label}</span>
                                            <motion.span
                                                animate={{ rotate: isOpen ? 180 : 0 }}
                                                transition={{ duration: 0.15 }}
                                                className="ms-2"
                                            >
                                                <FaChevronDown />
                                            </motion.span>
                                        </>
                                    )}
                                </button>

                                <AnimatePresence initial={false}>
                                    {!collapsed && isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.18 }}
                                            className="ps-3 mt-1 overflow-hidden"
                                        >
                                            {l.children.map((c) => (
                                                <NavLink
                                                    key={c.to}
                                                    to={c.to}
                                                    onClick={handleNavigate}
                                                    className={({ isActive }) =>
                                                        [
                                                            "d-flex align-items-center rounded px-3 py-2 text-decoration-none mb-1",
                                                            "gap-2",
                                                            isActive ? "bg-primary text-white" : "text-dark hover-bg",
                                                        ].join(" ")
                                                    }
                                                >
                                                    <span className="fs-6">{c.icon}</span>
                                                    <span className="fw-medium">{c.label}</span>
                                                </NavLink>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    }

                    return (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            onClick={handleNavigate}
                            title={collapsed ? l.label : undefined}
                            className={({ isActive }) =>
                                [
                                    "d-flex align-items-center rounded px-3 py-2 text-decoration-none mb-1",
                                    collapsed ? "justify-content-center" : "gap-2",
                                    isActive ? "bg-primary text-white" : "text-dark hover-bg",
                                ].join(" ")
                            }
                        >
                            <span className="fs-6">{l.icon}</span>
                            {!collapsed && <span className="fw-medium">{l.label}</span>}
                        </NavLink>
                    );
                })}
            </div>

            <div className="p-3 small text-muted bg-white border-top">
                {!collapsed ? <>© {new Date().getFullYear()}</> : <span title="©">©</span>}
            </div>

            {/* Collapsed-mode hover submenu */}
            {collapsed && hoverMenu?.children?.length ? (
                <div
                    className="sidebar-hover-menu position-fixed"
                    style={{ top: hoverMenu.top, left: hoverMenu.left, zIndex: 2000 }}
                    onMouseEnter={() => clearHoverCloseTimer()}
                    onMouseLeave={() => scheduleHoverClose()}
                >
                    <div className="sidebar-hover-header">{hoverMenu.label}</div>
                    <div className="p-2">
                        {hoverMenu.children.map((c) => (
                            <NavLink
                                key={c.to}
                                to={c.to}
                                onClick={handleNavigate}
                                className={({ isActive }) =>
                                    [
                                        "d-flex align-items-center rounded px-3 py-2 text-decoration-none mb-1",
                                        "gap-2",
                                        isActive ? "bg-primary text-white" : "text-dark hover-bg",
                                    ].join(" ")
                                }
                            >
                                <span className="fs-6">{c.icon}</span>
                                <span className="fw-medium">{c.label}</span>
                            </NavLink>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default function Sidebar({ variant, open, onClose, collapsed = false }) {
    if (variant === "desktop") {
        const expandedWidth = 280;
        const collapsedWidth = 84;

        return (
            <motion.aside
                className="border-end bg-white h-100"
                animate={{ width: collapsed ? collapsedWidth : expandedWidth }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                style={{ height: "100%", overflow: "visible" }}
            >
                <SidebarContent collapsed={collapsed} />
            </motion.aside>
        );
    }

    return (
        <>
            {open && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100"
                    style={{ background: "rgba(0,0,0,0.45)", zIndex: 1040 }}
                    onClick={onClose}
                />
            )}

            <div
                className="position-fixed top-0 start-0 h-100 bg-white border-end"
                style={{
                    width: 280,
                    zIndex: 1050,
                    transition: "transform 0.2s ease",
                    transform: open ? "translateX(0)" : "translateX(-100%)",
                    overflow: "hidden",
                }}
            >
                <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
                    <span className="fw-semibold ms-2">Menu</span>
                    <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <SidebarContent collapsed={false} onNavigate={onClose} />
            </div>
        </>
    );
}
